import { useState, useEffect, useCallback, useRef } from 'react'
import type { IntegrationSummary } from '../../integrations/types/index'
import { getHubSpotOAuthUrl, disconnectHubSpot } from '../services/hubspotApi'

type ActionStatus = 'idle' | 'connecting' | 'disconnecting' | 'error'

const CHANNEL_NAME = 'hubspot_oauth'

export function useHubSpotIntegration(
  workspaceId: string,
  summary: IntegrationSummary | null,
  onRefresh: () => void,
) {
  const [actionStatus, setActionStatus] = useState<ActionStatus>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const popupRef = useRef<Window | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearError = useCallback(() => setErrorMessage(null), [])

  // Primary: BroadcastChannel — reliable even when window.opener is null after redirects
  useEffect(() => {
    let channel: BroadcastChannel | null = null
    try {
      channel = new BroadcastChannel(CHANNEL_NAME)
      channel.onmessage = (event: MessageEvent) => {
        if (event.data?.type !== 'hubspot_oauth') return
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
        if (event.data.result === 'connected') {
          setActionStatus('idle')
          setErrorMessage(null)
          onRefresh()
        } else {
          setActionStatus('error')
          setErrorMessage('HubSpot connection failed. Please try again.')
        }
      }
    } catch {
      // BroadcastChannel not supported — fall back to postMessage listener below
    }
    return () => { channel?.close() }
  }, [onRefresh])

  // Fallback: window.postMessage
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'hubspot_oauth') return
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
      if (event.data.result === 'connected') {
        setActionStatus('idle')
        setErrorMessage(null)
        onRefresh()
      } else {
        setActionStatus('error')
        setErrorMessage('HubSpot connection failed. Please try again.')
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [onRefresh])

  const connect = useCallback(async () => {
    setActionStatus('connecting')
    clearError()
    try {
      const url = await getHubSpotOAuthUrl(workspaceId)
      const popup = window.open(url, 'hubspot_oauth', 'width=600,height=700,left=200,top=100')
      popupRef.current = popup
 
      pollRef.current = setInterval(() => {
        if (popup?.closed) {
          if (pollRef.current) clearInterval(pollRef.current)
          setActionStatus((s) => s === 'connecting' ? 'idle' : s)
        }
      }, 500)
    } catch {
      setActionStatus('error')
      setErrorMessage('Could not start HubSpot authorization. Please try again.')
    }
  }, [workspaceId, clearError])

  const disconnect = useCallback(async () => {
    setActionStatus('disconnecting')
    clearError()
    try {
      await disconnectHubSpot(workspaceId)
      setActionStatus('idle')
      onRefresh()
    } catch {
      setActionStatus('error')
      setErrorMessage('Failed to disconnect HubSpot. Please try again.')
    }
  }, [workspaceId, clearError, onRefresh])

  return {
    actionStatus,
    errorMessage,
    clearError,
    connect,
    disconnect,
    summary,
  }
}
